import { cn } from "@/lib/utils";
import {
  BarChart3,
  Bell,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  Gift,
  Home,
  MapPin,
  MoreHorizontal,
  Search,
  ShoppingBag,
  Star,
  User,
  Users,
  Utensils,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { type Brand, dayName, fmtTime, money, sampleMenu } from "./data";

/* ================================================================== frames */

export const BrowserChrome = ({
  url,
  children,
  className,
}: { url: string; children: ReactNode; className?: string }) => (
  <div
    className={cn(
      "overflow-hidden rounded-xl border border-[#e6e9f0] bg-white shadow-sm",
      className,
    )}
  >
    <div className="flex items-center gap-2 border-b border-[#e6e9f0] bg-[#f7f8fb] px-3 py-2">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
      <div className="ml-2 flex flex-1 items-center gap-2 rounded-md bg-white px-3 py-1 text-[11px] text-[#5b6472] shadow-inner">
        <span className="h-3 w-3 rounded-sm border border-[#c9d0dc]" />
        {url}
      </div>
    </div>
    {children}
  </div>
);

/** Tablet/POS bezel with a stand — the SUNMI D3-style terminal. */
export const MonitorFrame = ({ children }: { children: ReactNode }) => (
  <div className="mx-auto w-full max-w-[720px]">
    <div className="overflow-hidden rounded-[22px] border-[10px] border-[#111318] bg-[#111318] shadow-[0_30px_60px_-20px_rgba(15,23,42,.45)]">
      <div className="aspect-[16/10] bg-white">{children}</div>
    </div>
    <div className="mx-auto h-6 w-28 rounded-b-md bg-[#1c1f26]" />
    <div className="mx-auto h-2 w-56 rounded-full bg-[#2a2e37]" />
  </div>
);

export const PhoneFrame = ({
  children,
  className,
}: { children: ReactNode; className?: string }) => (
  <div
    className={cn(
      "relative h-[520px] w-[244px] shrink-0 overflow-hidden rounded-[34px] border-[9px] border-[#111318] bg-white shadow-[0_24px_50px_-20px_rgba(15,23,42,.5)]",
      className,
    )}
  >
    <div className="absolute left-1/2 top-0 z-10 h-5 w-24 -translate-x-1/2 rounded-b-xl bg-[#111318]" />
    <div className="flex items-center justify-between px-5 pt-2 text-[10px] font-semibold text-[#111318]">
      <span>9:41</span>
      <span className="tracking-tight">●●● ▲ ▮</span>
    </div>
    <div className="h-[calc(100%-1.5rem)] overflow-y-auto">{children}</div>
  </div>
);

/* =========================================================== device art */

export const DeviceArt = ({
  kind,
  className,
}: {
  kind: "pos" | "handheld" | "kiosk" | "printer" | "drawer" | "scanner" | "display";
  className?: string;
}) => {
  const c = cn("h-full w-full", className);
  switch (kind) {
    case "pos":
      return (
        <svg viewBox="0 0 64 48" className={c} fill="none" aria-hidden="true">
          <rect x="6" y="4" width="52" height="32" rx="4" fill="#1c1f26" />
          <rect x="10" y="8" width="44" height="24" rx="2" fill="#fff" />
          <rect x="12" y="10" width="10" height="20" rx="1" fill="#e8ecf4" />
          <rect x="24" y="10" width="28" height="4" rx="1" fill="#e8ecf4" />
          <rect x="26" y="36" width="12" height="6" fill="#2a2e37" />
          <rect x="18" y="42" width="28" height="3" rx="1.5" fill="#2a2e37" />
        </svg>
      );
    case "handheld":
      return (
        <svg viewBox="0 0 64 48" className={c} fill="none" aria-hidden="true">
          <rect x="22" y="2" width="20" height="44" rx="4" fill="#1c1f26" />
          <rect x="25" y="8" width="14" height="24" rx="1" fill="#fff" />
          <rect x="26" y="10" width="12" height="3" fill="#e8ecf4" />
          <rect x="26" y="15" width="12" height="3" fill="#e8ecf4" />
          <rect x="27" y="36" width="10" height="4" rx="1" fill="#3a3f4a" />
        </svg>
      );
    case "kiosk":
      return (
        <svg viewBox="0 0 64 48" className={c} fill="none" aria-hidden="true">
          <rect x="18" y="2" width="28" height="36" rx="3" fill="#1c1f26" />
          <rect x="21" y="6" width="22" height="26" rx="1" fill="#fff" />
          <rect x="23" y="9" width="18" height="10" rx="1" fill="#e8ecf4" />
          <rect x="23" y="22" width="18" height="3" fill="var(--brand)" />
          <rect x="28" y="38" width="8" height="6" fill="#2a2e37" />
          <rect x="20" y="44" width="24" height="3" rx="1.5" fill="#2a2e37" />
        </svg>
      );
    case "printer":
      return (
        <svg viewBox="0 0 64 48" className={c} fill="none" aria-hidden="true">
          <rect x="10" y="14" width="44" height="26" rx="4" fill="#1c1f26" />
          <rect x="16" y="8" width="32" height="8" rx="2" fill="#2a2e37" />
          <rect x="18" y="20" width="28" height="4" rx="1" fill="#fff" />
          <rect x="18" y="4" width="28" height="6" rx="1" fill="#fff" />
        </svg>
      );
    case "drawer":
      return (
        <svg viewBox="0 0 64 48" className={c} fill="none" aria-hidden="true">
          <rect x="6" y="16" width="52" height="24" rx="3" fill="#1c1f26" />
          <rect x="12" y="22" width="40" height="12" rx="2" fill="#2a2e37" />
          <rect x="28" y="26" width="8" height="4" rx="1" fill="#9aa3b2" />
        </svg>
      );
    case "scanner":
      return (
        <svg viewBox="0 0 64 48" className={c} fill="none" aria-hidden="true">
          <path d="M20 6h24v14H32l-4 22h-8l4-22h-4z" fill="#1c1f26" />
          <rect x="24" y="9" width="16" height="6" rx="1" fill="var(--brand)" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 64 48" className={c} fill="none" aria-hidden="true">
          <rect x="8" y="6" width="48" height="30" rx="3" fill="#1c1f26" />
          <rect x="12" y="10" width="40" height="22" rx="1" fill="#fff" />
          <rect x="24" y="36" width="16" height="5" fill="#2a2e37" />
        </svg>
      );
  }
};

/* ============================================================ site mock */

/** Logo stand-in when the prospect has none: their initials on the brand colour. */
export const Monogram = ({ b, className }: { b: Brand; className?: string }) => (
  <span
    className={cn(
      "inline-flex shrink-0 items-center justify-center rounded-full font-extrabold leading-none",
      className,
    )}
    style={{
      background: b.palette.primary,
      color: b.palette.onPrimary,
      fontFamily: "var(--font-heading)",
      fontSize: "0.5em",
      containerType: "inline-size",
    }}
  >
    <span style={{ fontSize: "55cqw" }}>{b.monogram}</span>
  </span>
);

const Img = ({ src, className, alt = "" }: { src?: string; className?: string; alt?: string }) =>
  src ? (
    <img src={src} alt={alt} className={cn("object-cover", className)} loading="lazy" />
  ) : (
    <div className={cn("bg-gradient-to-br from-[#2b2f3a] to-[#0f172a]", className)} />
  );

/**
 * The generated restaurant website — the prospect's real name, tagline,
 * description, photos, menu and hours from the import, laid out like the
 * mockup. Used inside the browser chrome (any width) and scaled down as the
 * Home thumbnail. If the demo already published a real site it replaces this.
 */
export function SitePreview({ b, compact = false }: { b: Brand; compact?: boolean }) {
  const menu = b.menu.length ? b.menu : sampleMenu(b.cuisine);
  const gallery = (b.images.length ? b.images : [b.hero, b.hero, b.hero, b.hero]).slice(0, 4);
  return (
    <div className="bg-white text-[#111318]" style={{ fontFamily: "var(--font-body)" }}>
      {/* nav */}
      <div className="flex items-center justify-between bg-[#0b0d12] px-5 py-3 text-white">
        <div className="flex items-center gap-2">
          {b.logo ? (
            <img src={b.logo} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <Monogram b={b} className="h-5 w-5" />
          )}
          <span className="text-sm font-bold uppercase tracking-wide">{b.name}</span>
        </div>
        {!compact ? (
          <div className="hidden items-center gap-4 text-[12px] text-white/80 md:flex">
            {["Home", "Menu", "Reservations", "Order Online", "About", "Contact"].map((l, i) => (
              <span
                key={l}
                className={cn(i === 0 && "border-b-2 pb-0.5 text-white")}
                style={i === 0 ? { borderColor: b.color } : undefined}
              >
                {l}
              </span>
            ))}
          </div>
        ) : null}
        <span
          className="rounded-md px-3 py-1.5 text-[11px] font-semibold text-white"
          style={{ background: b.color, color: b.palette.onPrimary }}
        >
          Reserve a Table
        </span>
      </div>
      {/* hero */}
      <div className="relative min-h-[300px] overflow-hidden md:min-h-[380px]">
        <Img src={b.hero} className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10" />
        <div className="relative flex h-full min-h-[300px] flex-col justify-center gap-3 px-6 py-8 text-white md:min-h-[380px] md:px-10">
          <h1
            className="text-4xl font-bold leading-[1.05] md:text-[56px]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {b.headline[0]}
            <br />
            {b.headline[1]}
          </h1>
          <p className="max-w-md text-base text-white/90 md:text-lg">{b.heroSub}</p>
          <div className="mt-2 flex gap-2">
            <span
              className="rounded-md px-4 py-2 text-[13px] font-semibold"
              style={{ background: b.color, color: b.palette.onPrimary }}
            >
              Make a Reservation
            </span>
            <span className="rounded-md border border-white/70 px-4 py-2 text-[13px] font-semibold">
              View Menu
            </span>
          </div>
        </div>
      </div>
      {/* story */}
      <div className="px-6 py-10 text-center md:px-16">
        <div
          className="text-[11px] font-semibold uppercase tracking-[0.25em]"
          style={{ color: b.palette.secondary }}
        >
          Our Story
        </div>
        <h2 className="mt-2 text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>
          A Passion for Great Food
        </h2>
        <div className="mx-auto mt-2 h-0.5 w-10" style={{ background: b.palette.secondary }} />
        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-[#4b5563]">
          {b.description}
        </p>
        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
          {gallery.map((src, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size static list
            <Img key={`${src}-${i}`} src={src} className="h-32 w-full rounded-lg md:h-40" />
          ))}
        </div>
      </div>
      {/* menu */}
      {!compact ? (
        <div className="bg-[#f7f5f2] px-6 py-10 md:px-16">
          <div
            className="text-center text-[11px] font-semibold uppercase tracking-[0.25em]"
            style={{ color: b.palette.secondary }}
          >
            Menu
          </div>
          <h2
            className="mt-2 text-center text-3xl font-bold"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {b.cuisine ? `${b.cuisine} Favorites` : "Our Favorites"}
          </h2>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            {menu.slice(0, 4).map((g) => (
              <div key={g.name}>
                <h3 className="mb-3 border-b border-[#e6e2dc] pb-2 text-sm font-bold uppercase tracking-wider">
                  {g.name}
                </h3>
                <ul className="space-y-3">
                  {g.items.slice(0, 5).map((it) => (
                    <li key={it.name} className="flex gap-3">
                      {it.imageUrl ? (
                        <img
                          src={it.imageUrl}
                          alt=""
                          className="h-12 w-12 rounded-md object-cover"
                          loading="lazy"
                        />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-semibold">{it.name}</span>
                          <span
                            className="text-sm font-semibold"
                            style={{ color: b.palette.secondary }}
                          >
                            {money(it.price)}
                          </span>
                        </div>
                        {it.description ? (
                          <p className="line-clamp-2 text-xs text-[#6b7280]">{it.description}</p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {/* hours + location + footer */}
      {!compact ? (
        <div className="grid gap-8 px-6 py-10 md:grid-cols-3 md:px-16">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider">Hours</h3>
            <ul className="mt-3 space-y-1 text-sm text-[#4b5563]">
              {b.hours.length ? (
                b.hours
                  .slice()
                  .sort((x, y) => x.day - y.day)
                  .map((h) => (
                    <li key={h.day} className="flex justify-between gap-4">
                      <span>{dayName(h.day)}</span>
                      <span>
                        {h.closed ? "Closed" : `${fmtTime(h.open)} – ${fmtTime(h.close)}`}
                      </span>
                    </li>
                  ))
              ) : (
                <>
                  <li className="flex justify-between">
                    <span>Mon – Thu</span>
                    <span>11 AM – 10 PM</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Fri – Sat</span>
                    <span>11 AM – 11 PM</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Sunday</span>
                    <span>12 PM – 9 PM</span>
                  </li>
                </>
              )}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider">
              {b.locations.length > 1 ? "Our Locations" : "Visit Us"}
            </h3>
            {b.locations.length > 1 ? (
              <ul className="mt-3 space-y-2 text-sm text-[#4b5563]">
                {b.locations.slice(0, 6).map((l) => (
                  <li key={l.name}>
                    <span className="font-semibold text-[#111318]">{l.name}</span>
                    {l.address ? <span> · {l.address}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-[#4b5563]">
                {b.address || b.location || "Your address"}
              </p>
            )}
            {b.phone ? <p className="mt-1 text-sm text-[#4b5563]">{b.phone}</p> : null}
            {b.rating ? (
              <p className="mt-2 flex items-center gap-1 text-sm">
                <Star className="h-4 w-4 fill-[#f59e0b] text-[#f59e0b]" /> {b.rating.toFixed(1)}
                {b.reviewCount ? (
                  <span className="text-[#6b7280]">({b.reviewCount} reviews)</span>
                ) : null}
              </p>
            ) : null}
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider">Order Online</h3>
            <p className="mt-3 text-sm text-[#4b5563]">
              Pickup and delivery, straight from our website — no third-party fees.
            </p>
            <span
              className="mt-3 inline-block rounded-md px-4 py-2 text-[13px] font-semibold text-white"
              style={{ background: b.color, color: b.palette.onPrimary }}
            >
              Order Now
            </span>
          </div>
          <div className="col-span-full border-t border-[#e6e9f0] pt-4 text-center text-xs text-[#9aa3b2]">
            © {new Date().getFullYear()} {b.name} · Powered by MyManager
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ============================================================ POS mock */

type OrderLine = { name: string; price: number; qty: number };

/** Interactive SUNMI POS screen driven by the imported menu. */
export function PosScreen({ b, onAction }: { b: Brand; onAction?: (a: string) => void }) {
  const menu = b.menu.length ? b.menu : sampleMenu(b.cuisine);
  const [cat, setCat] = useState(0);
  const [mode, setMode] = useState<"Dine In" | "Takeout" | "Delivery">("Dine In");
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [paid, setPaid] = useState(false);
  const [discount, setDiscount] = useState(false);
  const add = (name: string, price: number) => {
    setPaid(false);
    setLines((ls) => {
      const i = ls.findIndex((l) => l.name === name);
      if (i >= 0) return ls.map((l, j) => (j === i ? { ...l, qty: l.qty + 1 } : l));
      return [...ls, { name, price, qty: 1 }];
    });
    onAction?.("add_item");
  };
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const disc = discount ? subtotal * 0.1 : 0;
  const tax = (subtotal - disc) * 0.09;
  const total = subtotal - disc + tax;
  const group = menu[cat] ?? menu[0];
  return (
    <div className="flex h-full text-[#111318]">
      {/* left rail */}
      <div className="flex w-[18%] min-w-[74px] flex-col items-stretch gap-1 bg-[#0f172a] p-2 text-[10px] text-white/80">
        <div className="mb-2 flex items-center gap-1.5 px-1 py-1">
          {b.logo ? (
            <img src={b.logo} alt="" className="h-5 w-5 rounded-full object-cover" />
          ) : (
            <Monogram b={b} className="h-4 w-4" />
          )}
          <span className="truncate text-[10px] font-bold uppercase">{b.name}</span>
        </div>
        {[
          ["Home", Home],
          ["Orders", ShoppingBag],
          ["Tables", Utensils],
          ["Menu", Search],
          ["Payments", Gift],
          ["Reports", Star],
        ].map(([l, I], i) => {
          const Icon = I as typeof Home;
          return (
            <div
              key={l as string}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-2 py-1.5",
                i === 0 && "bg-white/10 text-white",
              )}
            >
              <Icon className="h-3 w-3" /> {l as string}
            </div>
          );
        })}
      </div>
      {/* catalog */}
      <div className="flex min-w-0 flex-1 flex-col bg-[#f7f8fb]">
        <div className="flex items-center gap-1 border-b border-[#e6e9f0] bg-white px-3 py-2">
          {(["Dine In", "Takeout", "Delivery"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                "rounded-md px-3 py-1 text-[11px] font-semibold",
                mode === m
                  ? "bg-[color:var(--brand)] text-[color:var(--on-brand)]"
                  : "text-[#5b6472] hover:bg-[#f0f2f7]",
              )}
            >
              {m}
            </button>
          ))}
          <div className="ml-auto flex gap-1 overflow-x-auto">
            {menu.map((g, i) => (
              <button
                key={g.name}
                type="button"
                onClick={() => setCat(i)}
                className={cn(
                  "whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[10px] font-medium",
                  i === cat
                    ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)] text-[color:var(--brand)]"
                    : "border-[#e6e9f0] text-[#5b6472]",
                )}
              >
                {g.name}
              </button>
            ))}
          </div>
        </div>
        <div className="grid flex-1 auto-rows-min grid-cols-3 gap-2 overflow-y-auto p-3">
          {group?.items.slice(0, 9).map((it) => (
            <button
              key={it.name}
              type="button"
              onClick={() => add(it.name, it.price)}
              className="group overflow-hidden rounded-lg border border-[#e6e9f0] bg-white text-left shadow-sm transition-transform active:scale-95"
            >
              <Img src={it.imageUrl ?? undefined} className="h-16 w-full" />
              <div className="p-2">
                <div className="truncate text-[11px] font-semibold">{it.name}</div>
                <div className="text-[11px] font-bold" style={{ color: b.color }}>
                  {money(it.price)}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* order */}
      <div className="flex w-[32%] min-w-[150px] flex-col border-l border-[#e6e9f0] bg-white">
        <div className="flex items-center justify-between border-b border-[#e6e9f0] px-3 py-2 text-[12px] font-bold">
          Current Order <span className="text-[10px] font-medium text-[#5b6472]">{mode}</span>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2 text-[11px]">
          {lines.length ? (
            lines.map((l, i) => (
              <div key={l.name} className="flex items-center justify-between py-1">
                <span className="truncate">
                  {i + 1} {l.name} {l.qty > 1 ? `×${l.qty}` : ""}
                </span>
                <span className="font-semibold">{money(l.price * l.qty)}</span>
              </div>
            ))
          ) : (
            <div className="py-6 text-center text-[11px] text-[#9aa3b2]">
              Tap a menu item to add it to the order.
            </div>
          )}
        </div>
        <div className="border-t border-[#e6e9f0] px-3 py-2 text-[11px]">
          <div className="flex justify-between text-[#5b6472]">
            <span>Subtotal</span>
            <span>{money(subtotal)}</span>
          </div>
          {discount ? (
            <div className="flex justify-between text-[#16a34a]">
              <span>Discount 10%</span>
              <span>-{money(disc)}</span>
            </div>
          ) : null}
          <div className="flex justify-between text-[#5b6472]">
            <span>Tax</span>
            <span>{money(tax)}</span>
          </div>
          <div className="mt-1 flex justify-between text-[13px] font-bold">
            <span>Total</span>
            <span>{money(total)}</span>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => {
                setLines([]);
                setPaid(false);
                setDiscount(false);
                onAction?.("clear");
              }}
              className="rounded-md border border-[#e6e9f0] py-1.5 text-[11px] font-semibold"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                setDiscount((v) => !v);
                onAction?.("discount");
              }}
              className={cn(
                "rounded-md border py-1.5 text-[11px] font-semibold",
                discount ? "border-[#16a34a] text-[#16a34a]" : "border-[#e6e9f0]",
              )}
            >
              %
            </button>
            <button
              type="button"
              disabled={!lines.length}
              onClick={() => {
                setPaid(true);
                onAction?.("pay");
              }}
              className="rounded-md bg-[color:var(--brand)] py-1.5 text-[11px] font-semibold text-[color:var(--on-brand)] disabled:opacity-40"
            >
              Pay
            </button>
          </div>
          {paid ? (
            <div className="mt-2 rounded-md bg-[#ecfdf3] px-2 py-1.5 text-center text-[11px] font-semibold text-[#15803d]">
              Paid · sent to kitchen · receipt printed
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* =========================================================== phone screens */

const TabBar = ({ active }: { active: string }) => (
  <div className="absolute inset-x-0 bottom-0 grid grid-cols-5 border-t border-[#e6e9f0] bg-white px-2 py-2 text-[9px] text-[#5b6472]">
    {[
      ["Home", Home],
      ["Menu", Utensils],
      ["Orders", ShoppingBag],
      ["Rewards", Gift],
      ["More", MoreHorizontal],
    ].map(([l, I]) => {
      const Icon = I as typeof Home;
      const on = l === active;
      return (
        <div
          key={l as string}
          className={cn("flex flex-col items-center gap-0.5", on && "text-[color:var(--brand)]")}
        >
          <Icon className="h-4 w-4" /> {l as string}
        </div>
      );
    })}
  </div>
);

export const PhoneHome = ({ b }: { b: Brand }) => (
  <div className="relative h-full">
    <div className="px-5 pt-4 text-center">
      {b.logo ? (
        <img src={b.logo} alt="" className="mx-auto h-14 w-14 rounded-full object-cover" />
      ) : (
        <Monogram b={b} className="mx-auto h-10 w-10" />
      )}
      <div className="mt-2 text-lg font-extrabold uppercase tracking-wide">{b.name}</div>
      <div className="text-[10px] text-[#5b6472]">{b.tagline}</div>
    </div>
    <div className="mx-4 mt-3 overflow-hidden rounded-xl">
      <Img src={b.hero} className="h-28 w-full" />
    </div>
    <div className="mx-4 mt-3 space-y-2">
      <div
        className="rounded-lg py-2.5 text-center text-[12px] font-bold text-white"
        style={{ background: b.color, color: b.palette.onPrimary }}
      >
        Order Now
      </div>
      <div className="rounded-lg border border-[#e6e9f0] py-2.5 text-center text-[12px] font-semibold">
        View Menu
      </div>
      <div className="rounded-lg border border-[#e6e9f0] py-2.5 text-center text-[12px] font-semibold">
        Reservations
      </div>
    </div>
    <TabBar active="Home" />
  </div>
);

export const PhoneMenu = ({ b }: { b: Brand }) => {
  const menu = b.menu.length ? b.menu : sampleMenu(b.cuisine);
  const [cat, setCat] = useState(0);
  const g = menu[cat] ?? menu[0];
  return (
    <div className="relative h-full">
      <div className="flex items-center gap-2 px-4 pt-3 text-[12px] font-bold">
        <ChevronLeft className="h-4 w-4" /> Menu
      </div>
      <div className="mt-2 flex gap-1.5 overflow-x-auto px-4">
        {menu.map((m, i) => (
          <button
            key={m.name}
            type="button"
            onClick={() => setCat(i)}
            className={cn(
              "whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-semibold",
              i === cat ? "text-white" : "bg-[#f0f2f7] text-[#5b6472]",
            )}
            style={i === cat ? { background: b.color, color: b.palette.onPrimary } : undefined}
          >
            {m.name}
          </button>
        ))}
      </div>
      <div className="mt-2 space-y-2 px-4 pb-14">
        {g?.items.slice(0, 6).map((it) => (
          <div
            key={it.name}
            className="flex items-center gap-2 rounded-lg border border-[#e6e9f0] p-1.5"
          >
            <Img src={it.imageUrl ?? undefined} className="h-10 w-10 rounded-md" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[11px] font-semibold">{it.name}</div>
              <div className="text-[10px] font-bold" style={{ color: b.color }}>
                {money(it.price)}
              </div>
            </div>
          </div>
        ))}
      </div>
      <TabBar active="Menu" />
    </div>
  );
};

export const PhoneReservations = ({ b }: { b: Brand }) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1).getDay();
  const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const [sel, setSel] = useState(now.getDate());
  const [time, setTime] = useState("6:30 PM");
  return (
    <div className="relative h-full">
      <div className="flex items-center gap-2 px-4 pt-3 text-[12px] font-bold">
        <ChevronLeft className="h-4 w-4" /> Reservations
      </div>
      <div className="mt-2 px-4 text-center text-[11px] font-semibold">
        {now.toLocaleString("en-US", { month: "long", year: "numeric" })}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-0.5 px-4 text-center text-[9px] text-[#5b6472]">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d}>{d}</div>
        ))}
        {Array.from({ length: first }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size static list
          <div key={`e${i}`} />
        ))}
        {Array.from({ length: days }, (_, n) => n + 1).map((day) => (
          <button
            key={day}
            type="button"
            onClick={() => setSel(day)}
            className={cn("rounded-full py-0.5 text-[10px]", sel === day && "font-bold text-white")}
            style={sel === day ? { background: b.color, color: b.palette.onPrimary } : undefined}
          >
            {day}
          </button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-1.5 px-4">
        {["5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTime(t)}
            className={cn(
              "rounded-md border py-1.5 text-[10px] font-semibold",
              t === time
                ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-[color:var(--on-brand)]"
                : "border-[#e6e9f0] text-[color:var(--brand)]",
            )}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="mx-4 mt-3 rounded-lg bg-[color:var(--brand)] py-2.5 text-center text-[12px] font-bold text-[color:var(--on-brand)]">
        Continue
      </div>
      <TabBar active="More" />
    </div>
  );
};

export const PhoneRewards = ({ b }: { b: Brand }) => (
  <div className="relative h-full">
    <div className="flex items-center gap-2 px-4 pt-3 text-[12px] font-bold">
      <ChevronLeft className="h-4 w-4" /> Rewards
    </div>
    <div
      className="mx-4 mt-3 rounded-xl p-4 text-center text-white"
      style={{ background: b.color, color: b.palette.onPrimary }}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-80">{b.name}</div>
      <div className="text-3xl font-extrabold">250</div>
      <div className="text-[10px] opacity-90">points · Get 1 point for every $1 spent</div>
    </div>
    <div className="mt-3 px-4 text-[11px] font-semibold">Available Rewards</div>
    <div className="mt-1 space-y-2 px-4">
      {[
        ["Free Appetizer", "200 points"],
        ["$10 Off Your Order", "500 points"],
        ["Free Dessert", "150 points"],
      ].map(([t, p]) => (
        <div key={t} className="flex items-center gap-2 rounded-lg border border-[#e6e9f0] p-2">
          <Gift className="h-4 w-4" style={{ color: b.color }} />
          <div className="flex-1 text-[11px] font-semibold">{t}</div>
          <div className="text-[10px] text-[#5b6472]">{p}</div>
        </div>
      ))}
    </div>
    <TabBar active="Rewards" />
  </div>
);

export const PhoneNotifications = ({ b }: { b: Brand }) => (
  <div className="relative h-full">
    <div className="flex items-center gap-2 px-4 pt-3 text-[12px] font-bold">
      <Bell className="h-4 w-4" /> Notifications
    </div>
    <div className="mt-2 space-y-2 px-4">
      {[
        ["Your order is ready!", "Order #45821 · pickup at the counter", "2m"],
        [`${b.name} · Weekend special`, "Two entrées and a dessert, $39 — this Friday only.", "1h"],
        [
          "Table confirmed",
          `Reservation for 4 · ${new Date().toLocaleDateString("en-US", { weekday: "long" })} 6:30 PM`,
          "1d",
        ],
        ["You earned 45 points", "Only 155 more to your next free appetizer.", "3d"],
      ].map(([t, s, w]) => (
        <div key={t} className="rounded-lg border border-[#e6e9f0] p-2">
          <div className="flex justify-between text-[11px] font-semibold">
            <span>{t}</span>
            <span className="text-[#9aa3b2]">{w}</span>
          </div>
          <div className="text-[10px] text-[#5b6472]">{s}</div>
        </div>
      ))}
    </div>
    <TabBar active="More" />
  </div>
);

export const PhoneLocations = ({ b }: { b: Brand }) => (
  <div className="relative h-full">
    <div className="flex items-center gap-2 px-4 pt-3 text-[12px] font-bold">
      <MapPin className="h-4 w-4" /> Locations
    </div>
    <div className="mx-4 mt-3 h-28 rounded-xl bg-[linear-gradient(135deg,#dbeafe,#eef2ff)]" />
    <div className="mt-3 space-y-2 px-4">
      {b.locations.length
        ? b.locations.slice(0, 4).map((l) => (
            <div key={l.name} className="rounded-lg border border-[#e6e9f0] p-2">
              <div className="text-[11px] font-semibold">
                {b.name} · {l.name}
              </div>
              <div className="text-[10px] text-[#5b6472]">
                {l.address || b.location || "Address"}
              </div>
              {l.phone ? (
                <div className="text-[10px] text-[color:var(--brand)]">{l.phone}</div>
              ) : null}
            </div>
          ))
        : null}
      {!b.locations.length &&
        Array.from({ length: Math.min(3, Math.max(1, b.locationsCount)) }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size static list
          <div key={i} className="rounded-lg border border-[#e6e9f0] p-2">
            <div className="text-[11px] font-semibold">
              {b.name}
              {b.locationsCount > 1 ? ` · ${i === 0 ? b.city || "Main" : `Location ${i + 1}`}` : ""}
            </div>
            <div className="text-[10px] text-[#5b6472]">{b.address || b.location || "Address"}</div>
            {b.phone ? (
              <div className="text-[10px] text-[color:var(--brand)]">{b.phone}</div>
            ) : null}
          </div>
        ))}
    </div>
    <TabBar active="More" />
  </div>
);

export const PhoneOrderConfirmed = ({ b }: { b: Brand }) => (
  <div className="relative h-full">
    <div className="flex items-center gap-2 px-4 pt-3 text-[12px] font-bold">
      <ChevronLeft className="h-4 w-4" /> Order Confirmation
    </div>
    <div className="mt-8 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#dcfce7] text-2xl text-[#16a34a]">
        ✓
      </div>
      <div className="mt-3 text-[14px] font-bold">Order Confirmed!</div>
      <div className="text-[10px] text-[#5b6472]">Your order has been received.</div>
    </div>
    <div className="mx-4 mt-5 rounded-lg border border-[#e6e9f0] p-3 text-center">
      <div className="text-[10px] text-[#5b6472]">Order #45821</div>
      <div className="mt-1 text-[10px] text-[#5b6472]">Estimated Pickup Time</div>
      <div className="text-[16px] font-extrabold" style={{ color: b.color }}>
        6:30 PM
      </div>
    </div>
    <div className="mx-4 mt-3 rounded-lg bg-[color:var(--brand)] py-2.5 text-center text-[12px] font-bold text-[color:var(--on-brand)]">
      View Order
    </div>
    <div className="mx-4 mt-2 rounded-lg border border-[#e6e9f0] py-2.5 text-center text-[12px] font-semibold">
      Back to Home
    </div>
    <TabBar active="Orders" />
  </div>
);

export const AccountAvatar = ({
  initials,
  className,
}: { initials: string; className?: string }) => (
  <div
    className={cn(
      "flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--brand)] text-[12px] font-bold text-[color:var(--on-brand)]",
      className,
    )}
  >
    {initials || <User className="h-4 w-4" />}
  </div>
);

export const CalendarIcon = Calendar;

/* ===================================================== admin app screens */

/** Deterministic "today" numbers from the real menu so every prospect sees plausible figures. */
const figures = (b: Brand) => {
  const items = b.items.length ? b.items : sampleMenu(b.cuisine).flatMap((g) => g.items);
  const priced = items.filter((i) => i.price > 0);
  const avg = priced.length ? priced.reduce((n, i) => n + i.price, 0) / priced.length : 16;
  const orders = 38 + (b.name.length % 9);
  return {
    items,
    avg,
    orders,
    sales: Math.round(avg * orders * 1.7),
    customers: 6 + (b.name.length % 5),
    bookings: 9 + (b.name.length % 7),
  };
};

const AdminTabBar = ({ active }: { active: string }) => (
  <div className="absolute inset-x-0 bottom-0 grid grid-cols-5 border-t border-[#e6e9f0] bg-white px-2 py-2 text-[9px] text-[#5b6472]">
    {[
      ["Home", Home],
      ["Orders", ShoppingBag],
      ["Customers", Users],
      ["Reports", BarChart3],
      ["More", MoreHorizontal],
    ].map(([l, I]) => {
      const Icon = I as typeof Home;
      return (
        <div
          key={l as string}
          className={cn(
            "flex flex-col items-center gap-0.5",
            l === active && "text-[color:var(--brand)]",
          )}
        >
          <Icon className="h-4 w-4" /> {l as string}
        </div>
      );
    })}
  </div>
);

const STATUS: Record<string, string> = {
  New: "bg-[color:var(--brand-soft)] text-[color:var(--brand)]",
  Preparing: "bg-[#fef3c7] text-[#d97706]",
  Ready: "bg-[#dcfce7] text-[#16a34a]",
  Done: "bg-[#f0f2f7] text-[#5b6472]",
};

const liveOrders = (b: Brand) => {
  const { items } = figures(b);
  const pick = (i: number) => items[i % Math.max(1, items.length)];
  return [
    { id: "#1042", who: "Maya R.", status: "New", it: [pick(0), pick(3)], min: "2m" },
    { id: "#1041", who: "Daniel K.", status: "Preparing", it: [pick(1)], min: "6m" },
    { id: "#1040", who: "Walk-in", status: "Ready", it: [pick(2), pick(5)], min: "11m" },
    { id: "#1039", who: "Sofia M.", status: "Done", it: [pick(4)], min: "24m" },
    { id: "#1038", who: "Chris P.", status: "Done", it: [pick(6), pick(1)], min: "31m" },
  ].map((o) => ({
    ...o,
    it: o.it.filter(Boolean) as NonNullable<Brand["items"][number]>[],
    total: o.it.filter(Boolean).reduce((n, i) => n + (i?.price ?? 0), 0) || 24.5,
  }));
};

export const PhoneAdminHome = ({ b }: { b: Brand }) => {
  const f = figures(b);
  return (
    <div className="relative h-full bg-[#f7f8fb]">
      <div className="px-4 pt-3">
        <div className="text-[10px] text-[#5b6472]">Good morning, {b.first || "there"}</div>
        <div className="text-[13px] font-bold">{b.name}</div>
        {b.locations.length > 1 ? (
          <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[9px] font-semibold text-[color:var(--brand)]">
            <MapPin className="h-3 w-3" /> {b.locations[0]?.name} ▾
          </div>
        ) : null}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 px-4">
        {[
          ["Today's sales", money(f.sales)],
          ["Orders", String(f.orders)],
          ["New customers", String(f.customers)],
          ["Bookings", String(f.bookings)],
        ].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-white p-2 shadow-sm">
            <div className="text-[9px] text-[#5b6472]">{l}</div>
            <div className="text-[14px] font-extrabold" style={{ color: b.color }}>
              {v}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between px-4 text-[11px] font-semibold">
        <span>Live orders</span>
        <span className="text-[9px] text-[color:var(--brand)]">See all</span>
      </div>
      <div className="mt-1 space-y-1.5 px-4 pb-14">
        {liveOrders(b)
          .slice(0, 3)
          .map((o) => (
            <div key={o.id} className="rounded-lg bg-white p-2 shadow-sm">
              <div className="flex items-center justify-between text-[10px] font-semibold">
                <span>
                  {o.id} · {o.who}
                </span>
                <span className={cn("rounded-full px-1.5 py-0.5 text-[8px]", STATUS[o.status])}>
                  {o.status}
                </span>
              </div>
              <div className="truncate text-[9px] text-[#5b6472]">
                {o.it.map((i) => i.name).join(", ")}
              </div>
            </div>
          ))}
      </div>
      <AdminTabBar active="Home" />
    </div>
  );
};

export const PhoneAdminOrders = ({ b }: { b: Brand }) => {
  const [filter, setFilter] = useState("All");
  const orders = liveOrders(b).filter((o) => filter === "All" || o.status === filter);
  return (
    <div className="relative h-full bg-[#f7f8fb]">
      <div className="flex items-center justify-between px-4 pt-3 text-[12px] font-bold">
        <span>Orders</span>
        <Bell className="h-4 w-4 text-[#5b6472]" />
      </div>
      <div className="mt-2 flex gap-1.5 overflow-x-auto px-4">
        {["All", "New", "Preparing", "Ready"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={cn(
              "whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-semibold",
              s === filter ? "text-white" : "bg-white text-[#5b6472]",
            )}
            style={s === filter ? { background: b.color, color: b.palette.onPrimary } : undefined}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="mt-2 space-y-1.5 px-4 pb-14">
        {orders.map((o) => (
          <div key={o.id} className="rounded-lg bg-white p-2 shadow-sm">
            <div className="flex items-center justify-between text-[10px] font-semibold">
              <span>
                {o.id} · {o.who}
              </span>
              <span className="text-[#5b6472]">{o.min} ago</span>
            </div>
            <div className="truncate text-[9px] text-[#5b6472]">
              {o.it.map((i) => i.name).join(", ")}
            </div>
            <div className="mt-1 flex items-center justify-between">
              <span className={cn("rounded-full px-1.5 py-0.5 text-[8px]", STATUS[o.status])}>
                {o.status}
              </span>
              <span className="text-[10px] font-bold">{money(o.total)}</span>
            </div>
          </div>
        ))}
      </div>
      <AdminTabBar active="Orders" />
    </div>
  );
};

export const PhoneAdminReports = ({ b }: { b: Brand }) => {
  const f = figures(b);
  const bars = [62, 48, 71, 55, 84, 100, 77];
  const top = f.items.slice(0, 3);
  return (
    <div className="relative h-full bg-[#f7f8fb]">
      <div className="px-4 pt-3 text-[12px] font-bold">Reports</div>
      <div className="mx-4 mt-2 rounded-lg bg-white p-2.5 shadow-sm">
        <div className="flex items-center justify-between text-[9px] text-[#5b6472]">
          <span>This week</span>
          <span className="font-semibold text-[#16a34a]">▲ 12%</span>
        </div>
        <div className="text-[14px] font-extrabold">{money(f.sales * 6.4)}</div>
        <div className="mt-2 flex h-16 items-end gap-1">
          {bars.map((h, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size static list
              key={i}
              className="flex-1 rounded-t"
              style={{ height: `${h}%`, background: i === 5 ? b.color : "var(--brand-soft)" }}
            />
          ))}
        </div>
        <div className="mt-1 grid grid-cols-7 text-center text-[8px] text-[#9aa3b2]">
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size static list
            <span key={i}>{d}</span>
          ))}
        </div>
      </div>
      <div className="mt-3 px-4 text-[11px] font-semibold">Top items</div>
      <div className="mt-1 space-y-1 px-4">
        {top.map((it, i) => (
          <div
            key={it.name}
            className="flex items-center gap-2 rounded-lg bg-white p-1.5 shadow-sm"
          >
            <span className="w-3 text-[9px] font-bold text-[#9aa3b2]">{i + 1}</span>
            <Img src={it.imageUrl ?? undefined} className="h-7 w-7 rounded" />
            <span className="min-w-0 flex-1 truncate text-[10px] font-semibold">{it.name}</span>
            <span className="text-[9px] text-[#5b6472]">{40 - i * 9} sold</span>
          </div>
        ))}
      </div>
      {b.locations.length > 1 ? (
        <>
          <div className="mt-3 px-4 text-[11px] font-semibold">By location</div>
          <div className="mt-1 space-y-1 px-4 pb-14">
            {b.locations.slice(0, 3).map((l, i) => (
              <div
                key={l.name}
                className="flex items-center justify-between rounded-lg bg-white p-1.5 text-[10px] shadow-sm"
              >
                <span className="font-semibold">{l.name}</span>
                <span className="text-[#5b6472]">{money(f.sales * (1 - i * 0.18))}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="pb-14" />
      )}
      <AdminTabBar active="Reports" />
    </div>
  );
};

/* =================================================== connect app screens */

const ConnectTabBar = ({ active }: { active: string }) => (
  <div className="absolute inset-x-0 bottom-0 grid grid-cols-4 border-t border-[#e6e9f0] bg-white px-2 py-2 text-[9px] text-[#5b6472]">
    {[
      ["Discover", Search],
      ["Bookings", Calendar],
      ["Wallet", CreditCard],
      ["Profile", User],
    ].map(([l, I]) => {
      const Icon = I as typeof Home;
      return (
        <div
          key={l as string}
          className={cn(
            "flex flex-col items-center gap-0.5",
            l === active && "text-[color:var(--brand)]",
          )}
        >
          <Icon className="h-4 w-4" /> {l as string}
        </div>
      );
    })}
  </div>
);

const NEARBY = [
  ["Bella Cuts", "Salon · 0.8 mi", "4.8"],
  ["Sunrise Yoga", "Fitness · 1.2 mi", "4.9"],
];

export const PhoneConnectDiscover = ({ b }: { b: Brand }) => (
  <div className="relative h-full">
    <div className="px-4 pt-3">
      <div className="text-[9px] text-[#5b6472]">MyManager Connect</div>
      <div className="text-[13px] font-bold">Near {b.city || "you"}</div>
    </div>
    <div className="mx-4 mt-2 flex items-center gap-2 rounded-lg bg-[#f0f2f7] px-2 py-1.5 text-[10px] text-[#9aa3b2]">
      <Search className="h-3.5 w-3.5" /> Restaurants, salons, gyms…
    </div>
    <div className="mt-2 flex gap-1.5 overflow-x-auto px-4">
      {["Restaurants", "Salons", "Fitness", "Shops", "Events"].map((c, i) => (
        <span
          key={c}
          className={cn(
            "whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-semibold",
            i === 0
              ? "bg-[color:var(--brand)] text-[color:var(--on-brand)]"
              : "bg-[#f0f2f7] text-[#5b6472]",
          )}
        >
          {c}
        </span>
      ))}
    </div>
    <div className="mt-3 px-4 text-[10px] font-semibold">Featured</div>
    <div className="mx-4 mt-1 overflow-hidden rounded-xl border border-[#e6e9f0]">
      <Img src={b.hero} className="h-20 w-full" />
      <div className="p-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold">{b.name}</span>
          {b.rating ? (
            <span className="flex items-center gap-0.5 text-[9px] font-semibold text-[#d97706]">
              <Star className="h-3 w-3 fill-current" /> {b.rating.toFixed(1)}
            </span>
          ) : null}
        </div>
        <div className="text-[9px] text-[#5b6472]">
          {[b.cuisine, b.location].filter(Boolean).join(" · ")}
        </div>
        <div className="mt-1.5 flex gap-1.5">
          <span
            className="rounded-md px-2 py-1 text-[9px] font-bold text-white"
            style={{ background: b.color, color: b.palette.onPrimary }}
          >
            Order
          </span>
          <span className="rounded-md border border-[#e6e9f0] px-2 py-1 text-[9px] font-semibold">
            Reserve
          </span>
          <span className="rounded-md border border-[#e6e9f0] px-2 py-1 text-[9px] font-semibold">
            Rewards
          </span>
        </div>
      </div>
    </div>
    <div className="mt-2 space-y-1 px-4 pb-14">
      {NEARBY.map(([n, s, r]) => (
        <div key={n} className="flex items-center gap-2 rounded-lg border border-[#e6e9f0] p-1.5">
          <div className="h-7 w-7 rounded bg-[#f0f2f7]" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[10px] font-semibold">{n}</div>
            <div className="text-[9px] text-[#5b6472]">{s}</div>
          </div>
          <span className="text-[9px] text-[#d97706]">★ {r}</span>
        </div>
      ))}
    </div>
    <ConnectTabBar active="Discover" />
  </div>
);

export const PhoneConnectBusiness = ({ b }: { b: Brand }) => {
  const items = (b.items.length ? b.items : sampleMenu(b.cuisine).flatMap((g) => g.items)).slice(
    0,
    3,
  );
  const today = b.hours.find((h) => h.day === new Date().getDay());
  return (
    <div className="relative h-full">
      <div className="relative">
        <Img src={b.hero} className="h-24 w-full" />
        <div className="absolute left-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-white/90">
          <ChevronLeft className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="px-4 pt-2">
        <div className="text-[13px] font-bold">{b.name}</div>
        <div className="text-[9px] text-[#5b6472]">
          {[b.cuisine, b.address || b.location].filter(Boolean).join(" · ")}
        </div>
        <div className="mt-0.5 flex items-center gap-1 text-[9px]">
          <Clock className="h-3 w-3 text-[#16a34a]" />
          <span className="font-semibold text-[#16a34a]">
            {today && !today.closed ? `Open · until ${fmtTime(today.close)}` : "Open now"}
          </span>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5 px-4">
        {["Order", "Reserve", "Rewards"].map((a, i) => (
          <span
            key={a}
            className={cn(
              "rounded-lg py-1.5 text-center text-[10px] font-bold",
              i === 0 ? "text-white" : "border border-[#e6e9f0]",
            )}
            style={i === 0 ? { background: b.color, color: b.palette.onPrimary } : undefined}
          >
            {a}
          </span>
        ))}
      </div>
      <div className="mt-3 px-4 text-[10px] font-semibold">Popular</div>
      <div className="mt-1 space-y-1.5 px-4 pb-14">
        {items.map((it) => (
          <div
            key={it.name}
            className="flex items-center gap-2 rounded-lg border border-[#e6e9f0] p-1.5"
          >
            <Img src={it.imageUrl ?? undefined} className="h-9 w-9 rounded-md" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[10px] font-semibold">{it.name}</div>
              <div className="text-[9px] font-bold" style={{ color: b.color }}>
                {money(it.price)}
              </div>
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-[#9aa3b2]" />
          </div>
        ))}
      </div>
      <ConnectTabBar active="Discover" />
    </div>
  );
};

export const PhoneConnectWallet = ({ b }: { b: Brand }) => (
  <div className="relative h-full">
    <div className="px-4 pt-3 text-[12px] font-bold">Wallet</div>
    <div
      className="mx-4 mt-2 rounded-xl p-3 text-white"
      style={{ background: `linear-gradient(135deg, ${b.color}, #111318)` }}
    >
      <div className="text-[9px] uppercase tracking-wider opacity-80">{b.name}</div>
      <div className="mt-1 flex items-end justify-between">
        <div>
          <div className="text-2xl font-extrabold">250</div>
          <div className="text-[9px] opacity-90">points</div>
        </div>
        <Gift className="h-6 w-6 opacity-90" />
      </div>
    </div>
    <div className="mt-3 px-4 text-[10px] font-semibold">Passes & cards</div>
    <div className="mt-1 space-y-1.5 px-4">
      {[
        ["Gift card", `${b.name} · $25.00 left`],
        ["Membership", "Regulars club · renews monthly"],
      ].map(([t, s]) => (
        <div key={t} className="flex items-center gap-2 rounded-lg border border-[#e6e9f0] p-2">
          <CreditCard className="h-4 w-4" style={{ color: b.color }} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold">{t}</div>
            <div className="truncate text-[9px] text-[#5b6472]">{s}</div>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-3 px-4 text-[10px] font-semibold">Upcoming</div>
    <div className="mx-4 mt-1 flex items-center gap-2 rounded-lg border border-[#e6e9f0] p-2">
      <Calendar className="h-4 w-4 text-[color:var(--brand)]" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold">Table for 4 · {b.name}</div>
        <div className="text-[9px] text-[#5b6472]">
          {new Date(Date.now() + 86400000).toLocaleDateString("en-US", {
            weekday: "long",
          })}{" "}
          · 6:30 PM
        </div>
      </div>
    </div>
    <ConnectTabBar active="Wallet" />
  </div>
);
